// src/components/ReadjudicacionMasiva/CrearLote/ResultadosSeleccion.js

import React, { useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
    Paper,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    TablePagination,
    Chip,
    Button,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    SelectAll as SelectAllIcon,
    Clear as ClearIcon
} from '@mui/icons-material';
import busquedaService from '../../../services/busquedaService';

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(2)
    },
    header: {
        marginBottom: theme.spacing(2),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    tableContainer: {
        maxHeight: 400
    },
    seleccionadosChip: {
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        fontWeight: 600
    }
}));

const ResultadosSeleccion = ({ expedientes, seleccionados, onSeleccionChange, totalCoincidencias = 0, filtrosUsados = null }) => {
    const classes = useStyles();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [trayendoTodos, setTrayendoTodos] = useState(false);
    const [avisoTodos, setAvisoTodos] = useState(null);

    // La búsqueda trae hasta 500 expedientes para mostrar, pero el lote puede necesitar
    // muchos más: los del sistema viejo llegan a 5.459. Esto pide al backend SOLO los
    // ids de todo lo que coincide y los selecciona.
    const hayMasQueLoCargado = totalCoincidencias > expedientes.length && !!filtrosUsados;

    const handleSeleccionarTodosLosCoincidentes = async () => {
        setTrayendoTodos(true);
        setAvisoTodos(null);
        try {
            const res = await busquedaService.buscarIds(filtrosUsados, 1);
            if (!res.ok) throw new Error(res.mensaje || 'No se pudieron traer los expedientes');

            const ids = (res.expedientes || []).map((e) => ({ Id: e.Id ?? e.id }));
            onSeleccionChange(ids);

            if (res.total > ids.length) {
                setAvisoTodos(`Se seleccionaron ${ids.length.toLocaleString('es-AR')} de ${res.total.toLocaleString('es-AR')}. Es el máximo por lote; afiná los filtros para abarcar el resto.`);
            }
        } catch (err) {
            setAvisoTodos(err.message || 'No se pudieron traer todos los expedientes');
        } finally {
            setTrayendoTodos(false);
        }
    };

    const handleSelectAll = () => {
        if (seleccionados.length === expedientes.length) {
            onSeleccionChange([]);
        } else {
            onSeleccionChange([...expedientes]);
        }
    };

    const handleSelectOne = (expediente) => {
        const index = seleccionados.findIndex(s => s.Id === expediente.Id);
        if (index >= 0) {
            // Deseleccionar
            onSeleccionChange(seleccionados.filter(s => s.Id !== expediente.Id));
        } else {
            // Seleccionar
            onSeleccionChange([...seleccionados, expediente]);
        }
    };

    const isSelected = (expediente) => {
        return seleccionados.some(s => s.Id === expediente.Id);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedExpedientes = expedientes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Paper className={classes.paper}>
            <Box className={classes.header}>
                <Box>
                    <Typography variant="h6" gutterBottom>
                        Resultados de Búsqueda
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Seleccione los expedientes para incluir en el lote
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" style={{ gap: '16px' }}>
                    <Chip
                        label={`${seleccionados.length} seleccionados`}
                        className={classes.seleccionadosChip}
                    />
                    <Button
                        size="small"
                        startIcon={seleccionados.length === expedientes.length ? <ClearIcon /> : <SelectAllIcon />}
                        onClick={handleSelectAll}
                    >
                        {seleccionados.length === expedientes.length ? 'Deseleccionar' : 'Seleccionar'} los {expedientes.length.toLocaleString('es-AR')} visibles
                    </Button>
                    {hayMasQueLoCargado && (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={trayendoTodos ? <CircularProgress size={16} /> : <SelectAllIcon />}
                            onClick={handleSeleccionarTodosLosCoincidentes}
                            disabled={trayendoTodos}
                        >
                            {trayendoTodos
                                ? 'Trayendo...'
                                : `Seleccionar los ${totalCoincidencias.toLocaleString('es-AR')} que coinciden`}
                        </Button>
                    )}
                </Box>
            </Box>

            {hayMasQueLoCargado && !avisoTodos && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: '4px' }}>
                    La búsqueda encontró <b>{totalCoincidencias.toLocaleString('es-AR')}</b> expedientes
                    y en la tabla se muestran <b>{expedientes.length.toLocaleString('es-AR')}</b>.
                    Para armar el lote con todos, usá el botón "Seleccionar los {totalCoincidencias.toLocaleString('es-AR')} que coinciden".
                </Alert>
            )}
            {avisoTodos && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: '4px' }} onClose={() => setAvisoTodos(null)}>
                    {avisoTodos}
                </Alert>
            )}

            <TableContainer className={classes.tableContainer}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    indeterminate={seleccionados.length > 0 && seleccionados.length < expedientes.length}
                                    checked={expedientes.length > 0 && seleccionados.length === expedientes.length}
                                    onChange={handleSelectAll}
                                    color="primary"
                                />
                            </TableCell>
                            <TableCell><strong>Expediente</strong></TableCell>
                            <TableCell><strong>Carátula</strong></TableCell>
                            <TableCell><strong>Demandado</strong></TableCell>
                            <TableCell align="right"><strong>Monto</strong></TableCell>
                            <TableCell><strong>Estado</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedExpedientes.map((expediente, idx) => {
                            const selected = isSelected(expediente);
                            return (
                                <TableRow
                                    key={expediente.Id}
                                    hover
                                    onClick={() => handleSelectOne(expediente)}
                                    selected={selected}
                                    style={{ cursor: 'pointer' }}
                                    sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}
                                >
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selected}
                                            color="primary"
                                        />
                                    </TableCell>
                                    <TableCell>{expediente.Autos || '-'}</TableCell>
                                    <TableCell>{expediente.Caratula || '-'}</TableCell>
                                    <TableCell>{expediente.Demandado || '-'}</TableCell>
                                    <TableCell align="right">
                                        {expediente.Monto_Total ? `$ ${parseFloat(expediente.Monto_Total).toLocaleString('es-AR')}` : '-'}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={expediente.Estado || 'N/A'}
                                            size="small"
                                            color={expediente.Estado === 'ACTIVO' ? 'primary' : 'default'}
                                        />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={expedientes.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
        </Paper>
    );
};

export default ResultadosSeleccion;
